/**
 * AdminFinanceRegister — Finance Register (iter-253-light-mode)
 *
 * Light mode: white background, dark text, Excel-like clean table.
 * Business rules:
 *   - Aniyor logistics rows (is_logistics=true): show "—" for all fee
 *     columns EXCEPT "Comm GST", which shows the GST Aniyor itself owes
 *     on its own logistics/shipping fee (SAC 996812) — iter-368.
 *   - Commission % from vendor subscription plan (not hardcoded 18%)
 *   - Net Payout column from V2 payout engine
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Download, Settings, RefreshCw, X, Plus, ChevronDown, AlertTriangle, Pencil, CheckCircle, Clock, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

// ─── Finance Tab Nav ──────────────────────────────────────────────────────────

export function FinanceNavTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const isRegister = location.pathname === '/admin/finance-register';
  const isRecon    = location.pathname === '/admin/finance-reconciliation';
  return (
    <div className="flex items-center gap-1 p-1 bg-white rounded-lg border border-gray-200 shadow-sm mb-6 w-fit"
      data-testid="finance-nav-tabs">
      <button onClick={() => navigate('/admin/finance-register')}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
          isRegister ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-900'
        }`}
        data-testid="tab-finance-register">Finance Register</button>
      <button onClick={() => navigate('/admin/finance-reconciliation')}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
          isRecon ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-900'
        }`}
        data-testid="tab-reconciliation-audit">Reconciliation &amp; Audit</button>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 4,  label: 'April' },   { value: 5,  label: 'May' },
  { value: 6,  label: 'June' },    { value: 7,  label: 'July' },
  { value: 8,  label: 'August' },  { value: 9,  label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' },
  { value: 12, label: 'December' },{ value: 1,  label: 'January' },
  { value: 2,  label: 'February' },{ value: 3,  label: 'March' },
];

function getFYOptions() {
  const now = new Date();
  const yr  = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return [
    `${yr}-${String(yr + 1).slice(-2)}`,
    `${yr - 1}-${String(yr).slice(-2)}`,
    `${yr - 2}-${String(yr - 1).slice(-2)}`,
  ];
}

const fmt = (v) =>
  v == null ? '—'
  : v === 0 ? <span className="text-gray-300">—</span>
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtAmt = (v) =>
  v == null || v === 0
    ? <span className="text-gray-300">—</span>
    : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtGross = (v) =>
  v == null
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtPct = (v) => (v == null || v === 0) ? <span className="text-gray-300">—</span> : `${(v * 100).toFixed(1)}%`;

// ─── Summary Strip ────────────────────────────────────────────────────────────

function SummaryStrip({ totals, onNeedsRefundClick }) {
  if (!totals || !totals.order_count) return null;
  const tiles = [
    { label: 'Total Revenue (Gross)',       value: totals.gross_sales,              accent: 'border-t-blue-500',    bg: 'bg-blue-50' },
    { label: 'Net Revenue (Gross–Refund)',   value: totals.net_revenue,              accent: 'border-t-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Total Refunds',               value: totals.total_refunds,            accent: 'border-t-red-400',     bg: 'bg-red-50' },
    { label: 'Commission Earned',           value: totals.commission_earned,        accent: 'border-t-violet-500',  bg: '' },
    { label: 'GST on Commission',           value: totals.commission_gst,           accent: 'border-t-gray-400',    bg: '' },
    { label: 'GST on Logistics Fee',        value: totals.logistics_gst,            accent: 'border-t-amber-400',   bg: '' },
    { label: 'Total GST Liability',         value: totals.total_gst_liability,      accent: 'border-t-indigo-500',  bg: 'bg-indigo-50' },
    { label: 'TCS',                         value: totals.tcs,                      accent: 'border-t-gray-400',    bg: '' },
    { label: 'TDS',                         value: totals.tds,                      accent: 'border-t-gray-400',    bg: '' },
    { label: 'PG Fee',                      value: totals.pg_fees,                  accent: 'border-t-gray-400',    bg: '' },
    { label: 'Total Vendor Payout',         value: totals.total_net_vendor_payout,  accent: 'border-t-emerald-600', bg: 'bg-emerald-50' },
  ];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 mb-4" data-testid="summary-tiles">
      {tiles.map(t => (
        <div key={t.label}
          className={`rounded-lg border border-gray-200 border-t-2 ${t.accent} bg-white ${t.bg} px-3 py-2.5 shadow-sm`}>
          <p className="text-gray-500 text-xs leading-tight mb-1">{t.label}</p>
          <p className="text-gray-900 font-bold text-sm font-mono">
            {t.value != null
              ? `₹${Number(t.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
              : '—'}
          </p>
        </div>
      ))}
      {/* Override + Missing Plan + Needs Refund Action badges */}
      {(totals.commission_override_count > 0 || totals.missing_plan_count > 0 || totals.needs_refund_action_count > 0) && (
        <div className="col-span-3 sm:col-span-5 lg:col-span-9 flex items-center gap-3 mt-1">
          {totals.needs_refund_action_count > 0 && (
            <button
              onClick={onNeedsRefundClick}
              className="flex items-center gap-1 px-3 py-1 bg-red-50 border border-red-300 rounded-full text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors"
              data-testid="finance-register-needs-refund-badge">
              <AlertTriangle size={11} />
              {totals.needs_refund_action_count} order{totals.needs_refund_action_count !== 1 ? 's' : ''} need{totals.needs_refund_action_count === 1 ? 's' : ''} refund action
            </button>
          )}
          {totals.missing_plan_count > 0 && (
            <span className="flex items-center gap-1 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-amber-700 text-xs font-medium">
              <AlertTriangle size={11} />
              {totals.missing_plan_count} order{totals.missing_plan_count !== 1 ? 's' : ''} missing subscription plan
            </span>
          )}
          {totals.commission_override_count > 0 && (
            <span className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-blue-700 text-xs font-medium">
              {totals.commission_override_count} manual commission override{totals.commission_override_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reconciliation Sweep Panel ────────────────────────────────────────────
// Phase 2 recovery for the cancel/refund SSOT gap — lists every order that
// still has `needs_refund_action` set (or predates the flag but is
// cancelled with a stale financial_status) so an admin can clear the
// backlog in one place instead of hunting order-by-order.
function ReconciliationSweepPanel({ apiCall, onClose, onOpenRefundModal }) {
  const [items,       setItems]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const [reasonDraft, setReasonDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/tax-management/admin/finance-register/reconciliation-sweep');
      setItems(res.items || []);
    } catch (e) { console.error('Reconciliation sweep error:', e); setItems([]); }
    setLoading(false);
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  const handleMarkNotRequired = async (item) => {
    const reason = (reasonDraft[item.order_id] || '').trim();
    if (reason.length < 5) {
      toast.error('Enter a reason (min 5 characters) — e.g. "COD order, never paid"');
      return;
    }
    setResolvingId(item.order_id);
    try {
      await apiCall(`/api/tax-management/admin/finance-register/reconciliation-sweep/${item.order_id}/mark-not-required`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      toast.success(`${item.order_number} marked as not requiring a refund`);
      await load();
    } catch (e) { toast.error(e.message || 'Failed to resolve'); }
    setResolvingId(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="reconciliation-sweep-panel">
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-gray-900 font-semibold flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-500" /> Reconciliation Sweep
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Cancelled orders whose refund/financial status never synced to the Financial SSOT
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-sweep-panel">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm text-gray-400">Scanning orders…</p>
          ) : (items && items.length === 0) ? (
            <p className="text-sm text-emerald-600 flex items-center gap-2" data-testid="sweep-all-clear">
              <CheckCircle size={16} /> All clear — no cancelled orders with a stale financial status.
            </p>
          ) : (
            <div className="space-y-3">
              {(items || []).map(item => (
                <div key={item.order_id} className="border border-gray-200 rounded-lg p-3"
                  data-testid={`sweep-item-${item.order_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{item.order_number}</p>
                      <p className="text-xs text-gray-500">
                        {item.vendor_name} &middot; Status: {item.status} &middot; Fin: {item.financial_status} &middot; Gross: ₹{Number(item.gross_amount).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.suggested_action === 'mark_not_required' ? (
                        <>
                          <input
                            type="text"
                            placeholder="Reason (e.g. never paid)"
                            className="border border-gray-300 rounded px-2 py-1 text-xs w-44"
                            value={reasonDraft[item.order_id] || ''}
                            onChange={e => setReasonDraft(prev => ({ ...prev, [item.order_id]: e.target.value }))}
                            data-testid={`sweep-reason-input-${item.order_number}`}
                          />
                          <button
                            disabled={resolvingId === item.order_id}
                            onClick={() => handleMarkNotRequired(item)}
                            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            data-testid={`sweep-mark-not-required-${item.order_number}`}
                          >
                            {resolvingId === item.order_id ? 'Saving…' : 'Mark Not Required'}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => onOpenRefundModal(item)}
                          className="px-3 py-1.5 text-xs border border-orange-300 text-orange-700 rounded-md hover:bg-orange-50"
                          data-testid={`sweep-review-${item.order_number}`}
                        >
                          Review / Record Refund
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Financial Status Badge ───────────────────────────────────────────────────

const FIN_STATUS_CFG = {
  paid:               'bg-emerald-100 text-emerald-700 border-emerald-200',
  refunded:           'bg-red-100 text-red-700 border-red-200',
  partially_refunded: 'bg-orange-100 text-orange-700 border-orange-200',
  pending:            'bg-yellow-100 text-yellow-700 border-yellow-200',
  cancelled:          'bg-gray-100 text-gray-600 border-gray-200',
};

function FinStatusBadge({ status }) {
  const cls = FIN_STATUS_CFG[status?.toLowerCase()] || 'bg-gray-100 text-gray-500 border-gray-200';
  return (
    <span className={`px-2 py-0.5 rounded text-xs border font-medium ${cls}`}>{status || '—'}</span>
  );
}

// ─── Reversal Status Badge ─────────────────────────────────────────────────────

const REVERSAL_STATUS_CFG = {
  pending:    'bg-yellow-50 text-yellow-700 border-yellow-200',
  processing: 'bg-blue-50 text-blue-700 border-blue-200',
  completed:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed:     'bg-red-50 text-red-700 border-red-200',
  eligible:   'bg-teal-50 text-teal-700 border-teal-200',
  partial:    'bg-orange-50 text-orange-700 border-orange-200',
};

function ReversalStatusBadge({ status, eligible }) {
  // Non-refunded rows: show eligibility
  if (!status && eligible === true)
    return <span className="px-2 py-0.5 rounded text-xs border font-medium bg-teal-50 text-teal-700 border-teal-200"
      data-testid="reversal-status-eligible">Eligible</span>;
  if (!status && eligible === false)
    return <span className="px-2 py-0.5 rounded text-xs border font-medium bg-gray-50 text-gray-400 border-gray-200"
      data-testid="reversal-status-review">Review</span>;
  if (!status) return <span className="text-gray-300 text-xs">—</span>;
  const cls = REVERSAL_STATUS_CFG[status?.toLowerCase()] || 'bg-gray-50 text-gray-500 border-gray-200';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`px-2 py-0.5 rounded text-xs border font-medium ${cls}`}
      data-testid={`reversal-status-${status}`}>{label}</span>
  );
}

// ─── Inline editable financial-status cell ────────────────────────────────────

function FinStatusCell({ row, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const handle = (newStatus) => {
    setOpen(false);
    if (newStatus !== row.financial_status) onStatusChange(row, newStatus);
  };
  // iter-406 — a multi-vendor parent row is a SUM(children) rollup; it
  // has no commission/payout of its own, so recording a refund against
  // the parent id alone would strand the rollup figures un-refunded.
  // Refund each vendor's own child row instead (same row-level
  // restriction already applied to commission overrides below).
  const refundStatusesDisabled = !!row.is_parent;
  return (
    <div className="relative" data-testid={`fin-status-cell-${row.order_id}`}>
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 group" title="Change financial status">
        <FinStatusBadge status={row.financial_status} />
        {row.needs_refund_action && (
          <span title="Automated refund attempt failed or was skipped — needs manual review"
            data-testid={`needs-refund-flag-${row.order_id}`}>
            <AlertTriangle size={11} className="text-red-500" />
          </span>
        )}
        <ChevronDown size={10} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px] flex flex-col py-1">
          {['paid', 'refunded', 'partially_refunded', 'pending', 'cancelled'].map(s => {
            const isRefundStatus = s === 'refunded' || s === 'partially_refunded';
            const disabled = isRefundStatus && refundStatusesDisabled;
            return (
              <button key={s} onClick={() => !disabled && handle(s)}
                disabled={disabled}
                title={disabled ? "Refund each vendor's child order individually — not the combined parent order" : undefined}
                data-testid={`fin-status-option-${row.order_id}-${s}`}
                className={`w-full text-left px-3 py-1.5 text-xs ${
                  disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-50'
                } ${
                  s === row.financial_status ? 'text-indigo-600 font-medium bg-indigo-50' : !disabled ? 'text-gray-700' : ''
                }`}>
                {s.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Refund Intent Modal ──────────────────────────────────────────────────────

const REFUND_SOURCES = [
  { value: 'shopify',       label: 'Shopify' },
  { value: 'razorpay',      label: 'Razorpay' },
  { value: 'seller_portal', label: 'Seller Portal' },
  { value: 'offline',       label: 'Offline' },
  { value: 'other',         label: 'Other' },
];

function RefundIntentModal({ order, onClose, onCalculateOnly, onRecordRefund, onProcessRefund, onCheckEligibility }) {
  const [option,          setOption]          = useState(null);
  const [refundAmt,       setRefundAmt]       = useState(order ? String(order.gross) : '');
  const [source,          setSource]          = useState('offline');
  const [returnReason,    setReturnReason]    = useState('OTHER');
  const [approvalReason,  setApprovalReason]  = useState('');
  const [overrideReason,  setOverrideReason]  = useState('');
  const [refEvidenceId,   setRefEvidenceId]   = useState('');
  const [refEvidenceType, setRefEvidenceType] = useState('upi');
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState('');
  const [recordResult,    setRecordResult]    = useState(null);
  const [processResult,   setProcessResult]   = useState(null);
  const [eligibility,     setEligibility]     = useState(null);
  const [eligLoading,     setEligLoading]     = useState(false);

  // Re-fetch eligibility when return_reason changes
  useEffect(() => {
    if (!order?.order_id || !onCheckEligibility) return;
    setEligLoading(true);
    onCheckEligibility(order.order_id, returnReason)
      .then(data => {
        setEligibility(data);
        if (data?.cost_allocation?.refund_amount > 0) {
          setRefundAmt(String(data.cost_allocation.refund_amount));
        } else if (data?.refund_amount > 0) {
          setRefundAmt(String(data.refund_amount));
        }
      })
      .catch(() => setEligibility(null))
      .finally(() => setEligLoading(false));
  }, [order?.order_id, returnReason]); // only run when order or return_reason changes

  // Auto-close modal after 1.5s when refund recorded successfully
  useEffect(() => {
    if (!recordResult) return;
    const t = setTimeout(() => onClose(), 1800);
    return () => clearTimeout(t);
  }, [recordResult, onClose]);

  if (!order) return null;

  // Eligibility banner config
  const eligBanner = (() => {
    if (eligLoading) return { cls: 'bg-gray-50 border-gray-200 text-gray-500', icon: '⏳', msg: 'Checking eligibility...' };
    if (!eligibility) return null;
    if (eligibility.eligible && !eligibility.manual_review)
      return { cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: '✓', msg: `Eligible for ${eligibility.refund_pct}% refund (${eligibility.reason?.replace(/_/g,' ')})` };
    if (eligibility.eligible && eligibility.manual_review)
      return { cls: 'bg-yellow-50 border-yellow-200 text-yellow-800', icon: '!', msg: `Manual review required — ${eligibility.reason?.replace(/_/g,' ')}` };
    return { cls: 'bg-red-50 border-red-200 text-red-800', icon: '✗', msg: `Outside refund window — ${eligibility.reason?.replace(/_/g,' ')} (override requires approval reason)` };
  })();

  const needsOverride = eligibility && (!eligibility.eligible || eligibility.manual_review);

  const handleRecord = async () => {
    setError('');
    const amt = parseFloat(refundAmt);
    if (!amt || amt <= 0) { setError('Enter a valid refund amount'); return; }
    if (!approvalReason.trim() || approvalReason.trim().length < 5) {
      setError('Approval reason is required (min 5 characters) for audit trail'); return;
    }
    if (needsOverride && !overrideReason.trim()) {
      setError('Override reason is required when manual review is needed'); return;
    }
    setLoading(true);
    try {
      const res = await onRecordRefund(order.order_id, {
        refund_amount:   amt,
        refund_source:   source,
        return_reason:   returnReason,
        approval_reason: approvalReason.trim(),
        override_used:   needsOverride && overrideReason.trim().length > 0,
        override_reason: overrideReason.trim() || undefined,
        evidence: refEvidenceId ? {
          reference_id:   refEvidenceId,
          reference_type: refEvidenceType,
        } : undefined,
      });
      setRecordResult(res);
      toast.success(`Refund recorded for ${order.order_number} — closing in 2s`);
    } catch (e) { setError(e.message || 'Failed to record refund'); }
    setLoading(false);
  };

  const handleProcess = async () => {
    setError('');
    if (!approvalReason.trim()) { setError('Enter a cancellation reason (required for audit trail)'); return; }
    setLoading(true);
    try {
      const result = await onProcessRefund(order.order_id, approvalReason);
      setProcessResult(result);
    } catch (e) { setError(e.message || 'Failed to process refund'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="refund-intent-modal">
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-gray-900 font-semibold">Mark as Refunded</h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Order {order.order_number} — Gross {order.gross ? `₹${Number(order.gross).toLocaleString('en-IN')}` : '—'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          {/* Eligibility banner */}
          {eligBanner && (
            <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${eligBanner.cls}`}
              data-testid="eligibility-banner">
              <span className="font-bold mt-0.5">{eligBanner.icon}</span>
              <span className="flex-1">
                <strong>Eligibility: </strong>{eligBanner.msg}
                {eligibility && (
                  <span className="ml-1 text-gray-400">
                    · {eligibility.listing_type} · {eligibility.authority}
                    {eligibility.days_since_order != null && ` · ${Math.round(eligibility.days_since_order)}d old`}
                  </span>
                )}
              </span>
            </div>
          )}
          {[
            {
              key: 'calc', title: 'A. Calculate Only',
              desc: 'Updates numbers on screen — session only, no DB write. For GST simulation.',
              badge: 'No DB change', badgeCls: 'bg-blue-100 text-blue-700',
            },
            {
              key: 'record', title: 'B. Record Refund',
              desc: 'Marks the order as refunded and creates a refund record in the database.',
              badge: 'Writes to DB', badgeCls: 'bg-amber-100 text-amber-700',
            },
            {
              key: 'process', title: 'C. Process Refund',
              desc: 'Full pipeline — cancels on Shopify, issues Razorpay refund, cancels Delhivery AWB.',
              badge: 'Live pipeline', badgeCls: 'bg-red-100 text-red-700',
            },
          ].map(opt => (
            <div key={opt.key}
              onClick={() => { setOption(opt.key); setProcessResult(null); setError(''); }}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                option === opt.key
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`refund-option-${opt.key}`}>
              <div className="flex items-center justify-between">
                <p className="text-gray-900 text-sm font-medium">{opt.title}</p>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${opt.badgeCls}`}>{opt.badge}</span>
              </div>
              <p className="text-gray-500 text-xs mt-0.5">{opt.desc}</p>
            </div>
          ))}

          {option === 'record' && (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Refund Amount (₹)</label>
                  <input type="number" step="0.01" min="0" value={refundAmt}
                    onChange={e => setRefundAmt(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                    data-testid="refund-amount-input" />
                </div>
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Source</label>
                  <select value={source} onChange={e => setSource(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                    data-testid="refund-source-select">
                    {REFUND_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Return Reason — drives eligibility + cost allocation */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">Return Reason</label>
                <select value={returnReason} onChange={e => setReturnReason(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                  data-testid="return-reason-select">
                  {[
                    { value: 'DAMAGED',           label: 'Damaged product received' },
                    { value: 'WRONG_PRODUCT',      label: 'Wrong product delivered' },
                    { value: 'CHANGED_MIND',       label: 'Customer changed mind' },
                    { value: 'DELIVERY_REFUSED',   label: 'Delivery refused / not available' },
                    { value: 'SELLER_CANCELLED',   label: 'Seller unable to fulfil' },
                    { value: 'NOT_DELIVERED',      label: 'Product not delivered' },
                    { value: 'OTHER',              label: 'Other' },
                  ].map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              {/* Cost allocation breakdown (from eligibility resolver) */}
              {eligibility?.cost_allocation?.deductions?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs space-y-1">
                  <div className="font-medium text-amber-800">Shipping Deductions</div>
                  {eligibility.cost_allocation.deductions.map((d, i) => (
                    <div key={i} className="flex justify-between text-amber-700">
                      <span className="capitalize">{d.type?.replace(/_/g, ' ')} (bearer: {d.bearer})</span>
                      <span>-₹{d.amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold text-amber-900 border-t border-amber-200 pt-1 mt-1">
                    <span>Refund to Customer</span>
                    <span>₹{eligibility.cost_allocation.refund_amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              )}

              {/* Approval Reason — mandatory for audit trail (P0) */}
              <div>
                <label className="text-gray-600 text-xs block mb-1">
                  Approval Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={approvalReason}
                  onChange={e => setApprovalReason(e.target.value)}
                  rows={2}
                  placeholder="Why is this refund being approved? (appears in audit trail)"
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm resize-none"
                  data-testid="refund-approval-reason" />
              </div>

              {/* Admin Override section — shown when manual_review=true or eligible=false */}
              {needsOverride && (
                <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-2"
                  data-testid="admin-override-section">
                  <div className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                    <span>Manual Override Required</span>
                    {!eligibility.eligible && <span className="text-red-600">(Order outside eligibility window)</span>}
                  </div>
                  <div>
                    <label className="text-amber-700 text-xs block mb-1">
                      Override Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      rows={2}
                      placeholder="Reason for overriding the refund policy (stored for vendor dispute resolution)"
                      className="w-full bg-white border border-amber-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm resize-none"
                      data-testid="override-reason-input" />
                  </div>
                </div>
              )}

              {/* Optional evidence reference */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Evidence Ref # (optional)</label>
                  <input type="text" value={refEvidenceId} onChange={e => setRefEvidenceId(e.target.value)}
                    placeholder="UTR / Razorpay ref"
                    className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                    data-testid="refund-evidence-id" />
                </div>
                <div>
                  <label className="text-gray-600 text-xs block mb-1">Evidence Type</label>
                  <select value={refEvidenceType} onChange={e => setRefEvidenceType(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                    data-testid="refund-evidence-type">
                    {['upi','bank_transfer','razorpay','shopify','cash','other'].map(t =>
                      <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
              {/* Reversal status after submit */}
              {recordResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 space-y-1">
                  <p className="text-xs font-medium text-green-800">Refund recorded</p>
                  <p className="text-xs text-green-700">
                    Reversal: <span className="font-medium">{recordResult.reversal_status || 'queued'}</span>
                    {' '}— {recordResult.reversal_note || ''}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">ID: {recordResult.refund_id?.slice(0,12)}…</p>
                </div>
              )}
            </div>
          )}

          {option === 'process' && !processResult && (
            <div className="space-y-2 pt-1">
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                This will cancel the order on Shopify, issue a Razorpay refund (if paid), and cancel the Delhivery AWB. This action is irreversible.
              </div>
              <div>
                <label className="text-gray-600 text-xs block mb-1">Cancellation Reason <span className="text-red-500">*</span></label>
                <input type="text" value={approvalReason} onChange={e => setApprovalReason(e.target.value)}
                  placeholder="e.g. Customer requested cancellation"
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-gray-900 text-sm"
                  data-testid="process-reason-input" />
              </div>
              {error && <p className="text-red-600 text-xs">{error}</p>}
            </div>
          )}

          {option === 'process' && processResult && (
            <div className="space-y-1.5 pt-1">
              <p className="text-xs font-medium text-gray-700">Pipeline Result</p>
              {[
                { label: 'Shopify Cancel',   value: processResult.shopify },
                { label: 'Razorpay Refund',  value: processResult.refund_status },
                { label: 'DB Reversals',     value: processResult.reversal?.status || (processResult.reversal ? 'done' : '—') },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                  <span className="text-xs text-gray-600">{label}</span>
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    String(value).includes('ok') || value === 'issued' || value === 'done' || value === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : value === 'skipped' || value === 'no_creds' || value === '—'
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-red-100 text-red-600'
                  }`}>{String(value || '—')}</span>
                </div>
              ))}
              <p className="text-xs text-gray-500 pt-1">Pipeline attempt: {processResult.pipeline_attempt_id?.slice(-8)}</p>
            </div>
          )}


          <div className="flex gap-2 pt-1">
            <button onClick={onClose}
              className="flex-1 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm rounded-lg"
              data-testid="refund-modal-cancel">{recordResult || processResult ? 'Close' : 'Cancel'}</button>
            {option === 'calc' && (
              <button
                onClick={() => { onCalculateOnly(order.order_id, parseFloat(refundAmt) || order.gross); onClose(); }}
                className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium"
                data-testid="confirm-calculate-only">
                Simulate Refund
              </button>
            )}
            {option === 'record' && !recordResult && (
              <button onClick={handleRecord} disabled={loading}
                className="flex-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg font-medium disabled:opacity-50"
                data-testid="confirm-record-refund">
                {loading ? 'Recording...' : 'Record Refund'}
              </button>
            )}
            {option === 'process' && !processResult && (
              <button onClick={handleProcess} disabled={loading}
                className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium disabled:opacity-50"
                data-testid="confirm-process-refund">
                {loading ? 'Processing...' : 'Process Refund'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Formula Config Modal ─────────────────────────────────────────────────────
// NOTE: commission_default_pct is intentionally excluded.
// Commission % comes from vendor_subscriptions only (not formula config).

const FORMULA_FIELDS = [
  { key: 'pg_fee_pct',         label: 'PG Fee %' },
  { key: 'pg_fee_gst_pct',     label: 'GST on PG Fee %' },
  { key: 'tcs_pct',            label: 'TCS %' },
  { key: 'tds_pct',            label: 'TDS %' },
  { key: 'commission_gst_pct', label: 'Commission GST %' },
  { key: 'logistics_gst_pct',  label: 'Logistics GST %' },
];

function FormulaConfigModal({ onClose, apiCall }) {
  const [configs,    setConfigs]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [creating,   setCreating]   = useState(false);
  const [activating, setActivating] = useState(null);
  const [form, setForm] = useState({
    pg_fee_pct: 2, pg_fee_gst_pct: 18,
    tcs_pct: 0.5, tds_pct: 0.1, commission_gst_pct: 18, logistics_gst_pct: 18,
    effective_from: '', notes: '',
  });

  const reload = async () => {
    try {
      const d = await apiCall('/api/tax-management/admin/formula-configs');
      setConfigs(d.configs || []);
    } catch (e) { /* non-fatal — formula configs panel is auxiliary */ }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    if (!form.effective_from) return alert('Effective From date is required');
    setCreating(true);
    try {
      const body = {};
      FORMULA_FIELDS.forEach(f => { body[f.key] = Number(form[f.key]) / 100; });
      body.effective_from = new Date(form.effective_from).toISOString();
      body.notes = form.notes;
      await apiCall('/api/tax-management/admin/formula-configs', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await reload();
    } catch (e) { alert(e.message); }
    setCreating(false);
  };

  const handleActivate = async (vnum) => {
    setActivating(vnum);
    try {
      await apiCall(`/api/tax-management/admin/formula-configs/${vnum}/activate`, { method: 'PUT' });
      await reload();
    } catch (e) { alert(e.message); }
    setActivating(null);
  };

  const latest = configs[0] || {};

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="formula-config-modal">
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-gray-900 font-semibold text-lg">Formula Configuration</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Active config */}
          <div>
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">
              Active: {latest.version_label || 'V1'} — From {latest.effective_from?.slice(0, 10) || '—'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {FORMULA_FIELDS.map(f => (
                <div key={f.key} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                  <span className="text-gray-600 text-xs">{f.label}</span>
                  <span className="text-gray-900 text-xs font-mono font-bold">
                    {latest[f.key] != null ? `${(latest[f.key] * 100).toFixed(2)}%` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* New version */}
          <div className="border border-gray-200 rounded-xl p-4">
            <p className="text-gray-900 font-medium text-sm mb-3 flex items-center gap-2">
              <Plus size={14} className="text-indigo-600" /> New Version
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {FORMULA_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="text-gray-600 text-xs block mb-1">{f.label}</label>
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.01" min="0" max="100"
                      value={form[f.key]}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1 text-gray-900 text-xs"
                      data-testid={`formula-input-${f.key}`} />
                    <span className="text-gray-400 text-xs">%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-gray-600 text-xs block mb-1">Effective From</label>
                <input type="date" value={form.effective_from}
                  onChange={e => setForm(p => ({ ...p, effective_from: e.target.value }))}
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1 text-gray-900 text-xs"
                  data-testid="formula-effective-from" />
              </div>
              <div>
                <label className="text-gray-600 text-xs block mb-1">Notes</label>
                <input type="text" value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1 text-gray-900 text-xs"
                  data-testid="formula-notes" />
              </div>
            </div>
            <button onClick={handleCreate} disabled={creating}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg font-medium disabled:opacity-50"
              data-testid="save-formula-config-btn">
              {creating ? 'Saving...' : 'Save as Draft'}
            </button>
          </div>

          {/* Version history */}
          <div>
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-3">Version History</p>
            {loading ? <p className="text-gray-400 text-xs">Loading...</p> : (
              <div className="space-y-2">
                {configs.map(c => (
                  <div key={c.version_label}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200"
                    data-testid={`formula-version-${c.version_label}`}>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold border ${
                        c.is_active
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>{c.version_label}</span>
                      <div>
                        <p className="text-gray-900 text-xs font-medium">
                          From {c.effective_from?.slice(0, 10) || '—'}
                        </p>
                        <p className="text-gray-500 text-xs">{c.notes || 'No notes'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 text-xs font-mono">
                        PG {((c.pg_fee_pct || 0) * 100).toFixed(1)}% · TCS {((c.tcs_pct || 0) * 100).toFixed(2)}% · TDS {((c.tds_pct || 0) * 100).toFixed(2)}%
                      </span>
                      {!c.is_active && (
                        <button onClick={() => handleActivate(c.version_number)}
                          disabled={activating === c.version_number}
                          className="px-2 py-0.5 text-xs rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 disabled:opacity-50"
                          data-testid={`activate-version-${c.version_label}`}>
                          {activating === c.version_number ? '...' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Commission Rate Badge ────────────────────────────────────────────────────
// Phase 7 — Authority Rules:
//   override:*           → "Custom Override" (orange)
//   subscription_history → "Subscription"    (green, historical accuracy)
//   subscription_stale   → "Subscription"    (amber, cached value differed from plan)
//   subscription | plan  → "Subscription"    (green, live plan)
//   MISSING_PLAN         → handled by CommissionOverrideDropdown below

function CommissionRateBadge({ row }) {
  const crs      = row.commission_rate_source || '';
  const ratePct  = row.commission_rate != null ? `${(row.commission_rate * 100).toFixed(1)}%` : '—';
  const planCode = row.subscription_plan_code || '';
  const planLabel = planCode ? planCode.charAt(0).toUpperCase() + planCode.slice(1) : '';

  if (crs.startsWith('override:')) {
    const planName = crs.split(':')[1] || 'Manual';
    return (
      <span className="group relative inline-flex items-center gap-1 cursor-default">
        <span className="text-orange-700 font-medium">{ratePct}</span>
        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold tracking-wide">Custom Override</span>
        <span className="invisible group-hover:visible absolute bottom-full right-0 mb-1 bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 whitespace-nowrap z-30 shadow-lg">
          Admin override — {planName}
        </span>
      </span>
    );
  }
  if (crs === 'vendor_override') {
    return (
      <span className="group relative inline-flex items-center gap-1 cursor-default">
        <span className="text-orange-700 font-medium">{ratePct}</span>
        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold tracking-wide">Custom Override</span>
        <span className="invisible group-hover:visible absolute bottom-full right-0 mb-1 bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 whitespace-nowrap z-30 shadow-lg">
          Vendor-level override — also applied by Payout Engine
        </span>
      </span>
    );
  }
  if (crs === 'subscription_stale') {
    return (
      <span className="group relative inline-flex items-center gap-1 cursor-default">
        <span className="text-amber-700 font-medium">{ratePct}</span>
        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold tracking-wide">Subscription</span>
        <span className="invisible group-hover:visible absolute bottom-full right-0 mb-1 bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 whitespace-nowrap z-30 shadow-lg">
          Plan rate applied — cached value was stale{planLabel ? ` (${planLabel})` : ''}
        </span>
      </span>
    );
  }
  if (crs === 'subscription_history') {
    return (
      <span className="group relative inline-flex items-center gap-1 cursor-default">
        <span className="text-green-700 font-medium">{ratePct}</span>
        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold tracking-wide">Subscription</span>
        <span className="invisible group-hover:visible absolute bottom-full right-0 mb-1 bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 whitespace-nowrap z-30 shadow-lg">
          {planLabel ? `${planLabel} plan` : 'Subscription'} active on order date
        </span>
      </span>
    );
  }
  // subscription | plan | migration_* → standard Subscription badge
  if (crs && crs !== 'MISSING_PLAN') {
    return (
      <span className="group relative inline-flex items-center gap-1 cursor-default">
        <span className="text-green-700 font-medium">{ratePct}</span>
        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold tracking-wide">Subscription</span>
        <span className="invisible group-hover:visible absolute bottom-full right-0 mb-1 bg-gray-900 text-white text-xs rounded-lg px-2 py-1.5 whitespace-nowrap z-30 shadow-lg">
          {planLabel ? `${planLabel} plan` : 'Active subscription plan'}
        </span>
      </span>
    );
  }
  // Fallback (no source label at all) — show rate without badge
  return (
    <span className="text-gray-700 font-medium">{ratePct}</span>
  );
}


// ─── Commission Override Dropdown ────────────────────────────────────────────
// Shown when commission_rate_source === "MISSING_PLAN"

const PRESET_RATES = [
  { label: '18% (Launch)',       value: 18 },
  { label: '15% (Basic)',        value: 15 },
  { label: '12% (Professional)', value: 12 },
  { label: '10% (Business)',     value: 10 },
  { label: '8% (Enterprise)',    value:  8 },
];

function CommissionOverrideDropdown({ row, state, onToggle, onApply, onStateChange, isCorrectionMode = false }) {
  const dropdownRef = useRef(null);
  const isCustom    = state.selectedRate === 'custom';

  // Close on outside click
  useEffect(() => {
    if (!state.open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onToggle();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [state.open, onToggle]);

  const handleSelect = (ratePct, label) => {
    if (ratePct === 'custom') {
      onStateChange({ selectedRate: 'custom' });
    } else {
      onStateChange({ selectedRate: ratePct, planLabel: label });
    }
  };

  const handleApply = () => {
    const ratePct   = isCustom ? parseFloat(state.customRate || 0) : state.selectedRate;
    const planLabel = isCustom ? `Custom ${ratePct}%` : state.planLabel;
    if (!ratePct || ratePct <= 0 || ratePct > 100) {
      alert('Enter a valid rate between 0.1% and 100%');
      return;
    }
    onApply(row.order_id, ratePct, planLabel, state.customReason || '');
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {!isCorrectionMode ? (
        <button
          onClick={onToggle}
          className="flex items-center gap-1 text-amber-600 hover:text-amber-800 text-xs font-medium"
          data-testid={`missing-plan-btn-${row.order_number}`}>
          <AlertTriangle size={11} />
          MISSING PLAN
          <ChevronDown size={10} />
        </button>
      ) : null}

      {state.open && (
        <div className="absolute top-full right-0 mt-1 z-30 bg-white border border-gray-200 rounded-xl shadow-xl w-52 flex flex-col py-1"
          data-testid={`override-dropdown-${row.order_number}`}>
          <p className="px-3 pt-2 pb-1 text-xs text-gray-500 font-medium">
            {isCorrectionMode ? 'Correct Commission Rate' : 'Select Commission Rate'}
          </p>
          {PRESET_RATES.map(p => (
            <button key={p.value}
              onClick={() => handleSelect(p.value, p.label)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                state.selectedRate === p.value ? 'text-indigo-600 font-semibold bg-indigo-50' : 'text-gray-700'
              }`}>
              {p.label}
            </button>
          ))}
          <div className="border-t border-gray-100 mt-1 mb-1" />
          <button
            onClick={() => handleSelect('custom', 'Custom')}
            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
              state.selectedRate === 'custom' ? 'text-indigo-600 font-semibold' : 'text-gray-700'
            }`}>
            Custom Rate...
          </button>

          {/* Custom rate input */}
          {isCustom && (
            <div className="px-3 pb-2 space-y-1.5">
              <input
                type="number" min="0.1" max="100" step="0.5"
                placeholder="e.g. 14.5"
                value={state.customRate || ''}
                onChange={e => onStateChange({ customRate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                data-testid={`custom-rate-input-${row.order_number}`}
              />
              <input
                type="text"
                placeholder="Reason (optional)"
                value={state.customReason || ''}
                onChange={e => onStateChange({ customReason: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              />
            </div>
          )}

          {/* Apply button */}
          {(state.selectedRate || isCustom) && (
            <div className="px-3 pb-2">
              <button
                onClick={handleApply}
                className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg"
                data-testid={`apply-override-btn-${row.order_number}`}>
                Apply Rate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminFinanceRegister() {
  const { apiCall, isSuperAdmin } = useAuth();
  const fyOptions   = getFYOptions();

  const [rangeType,   setRangeType]   = useState('fy');
  const [fy,          setFy]          = useState(fyOptions[0]);
  const [month,       setMonth]       = useState(null);
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState('');
  const [viewMode,    setViewMode]    = useState('order');
  const [loading,     setLoading]     = useState(false);
  const [data,        setData]        = useState(null);
  const [orderSearch, setOrderSearch] = useState('');

  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [refundIntent,     setRefundIntent]     = useState(null);
  const [sweepOpen,        setSweepOpen]        = useState(false);
  const [simulatedRefunds, setSimulatedRefunds] = useState({});

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setSimulatedRefunds({});
    try {
      const params = new URLSearchParams();
      if (rangeType === 'alltime') {
        params.append('all_time', 'true');
      } else if (rangeType === 'custom' && customStart && customEnd) {
        params.append('start_date', customStart + 'T00:00:00');
        params.append('end_date',   customEnd   + 'T23:59:59');
      } else {
        params.append('fy', fy);
        if (month) params.append('month', month);
      }
      const res = await apiCall(`/api/tax-management/admin/finance-register?${params}`);
      setData(res);
    } catch (e) { console.error('Finance Register error:', e); }
    setLoading(false);
  }, [rangeType, fy, month, customStart, customEnd, apiCall]);

  useEffect(() => { load(); }, [load]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleRevertFrOverride = async (row) => {
    if (!window.confirm(`Revert the Finance Register month override for order ${row.order_number}? This cannot be undone.`)) return;
    try {
      await apiCall('/api/admin/commission-invoices/audit/fr-vs-invoice-fix/revert-fr-override', {
        method: 'POST',
        body: JSON.stringify({ order_id: row.order_id }),
      });
      toast.success('Override reverted');
      load();
    } catch (e) {
      toast.error('Could not revert override', { description: e?.message || String(e) });
    }
  };

  const handleStatusChange = (row, newStatus) => {
    if (newStatus === 'refunded' || newStatus === 'partially_refunded') {
      setRefundIntent(row);
    }
  };

  const handleCalculateOnly = (orderId, refundAmt) => {
    setSimulatedRefunds(prev => ({ ...prev, [orderId]: refundAmt }));
  };

  const handleCheckEligibility = async (orderId, returnReason = 'OTHER') => {
    return apiCall(`/api/refund-eligibility/${orderId}?return_reason=${returnReason}`);
  };

  const handleRecordRefund = async (orderId, payload) => {
    const res = await apiCall(`/api/tax-management/admin/finance-register/${orderId}/record-refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    await load();
    return res;
  };

  const handleProcessRefund = async (orderId, reason) => {
    const res = await apiCall(`/api/tax-management/admin/finance-register/${orderId}/retry-refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    await load();
    return res;
  };

  // ── CSV Download — authenticated fetch (not window.open) ────────────────────
  const handleDownload = async () => {
    const params = new URLSearchParams();
    if (rangeType === 'alltime') params.append('all_time', 'true');
    else if (rangeType === 'custom' && customStart && customEnd) {
      params.append('start_date', customStart + 'T00:00:00');
      params.append('end_date',   customEnd   + 'T23:59:59');
    } else {
      params.append('fy', fy);
      if (month) params.append('month', month);
    }
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const url   = `${process.env.REACT_APP_BACKEND_URL}/api/tax-management/admin/finance-register/download?${params}`;
      const res   = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob    = await res.blob();
      const period  = rangeType === 'alltime' ? 'AllTime'
        : rangeType === 'custom' ? `${customStart}_to_${customEnd}`
        : `FY${fy}${month ? `_M${month}` : ''}`;
      const blobUrl = URL.createObjectURL(blob);
      const a       = document.createElement('a');
      a.href        = blobUrl;
      a.download    = `Aniyor_Finance_${period}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('CSV download failed:', e);
      alert('CSV download failed — please check your connection and try again.');
    }
  };

  // ── Commission Invoice preview (Requirement 3) ─────────────────────────────
  const previewCommissionInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/commission-invoices/${invoiceId}/html`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load invoice: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (e) {
      alert('Failed to open Commission Invoice: ' + e.message);
    }
  };

  // ── Commission Override ───────────────────────────────────────────────────
  const [commOverrideState, setCommOverrideState] = useState({}); // orderId → {open, customRate, customReason}

  const handleCommissionOverride = async (orderId, overrideRatePct, planLabel, customReason) => {
    try {
      await apiCall(`/api/tax-management/admin/finance-register/${orderId}/commission-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          override_rate: overrideRatePct,
          plan_label:    planLabel || 'Manual',
          reason:        customReason || '',
        }),
      });
      // Close dropdown, reload data
      setCommOverrideState(prev => ({ ...prev, [orderId]: { open: false } }));
      await load();
    } catch (e) {
      alert('Failed to apply override: ' + e.message);
    }
  };

  const displayRow = (r) => {
    const simAmt = simulatedRefunds[r.order_id];
    if (simAmt == null) return r;
    return {
      ...r, financial_status: 'refunded', refund_amount: simAmt,
      commission_amt: 0, commission_gst: 0, comm_waiver: 0, comm_gst_reversal: 0,
      tcs: 0, tds: 0, pg_fee: 0, pg_fee_gst: 0,
      net_vendor_payout: Math.round((r.gross - simAmt) * 100) / 100,
      _simulated: true,
    };
  };

  const rows          = data?.rows          || [];
  const totals        = data?.totals        || {};
  const vendorSummary = data?.vendor_summary || [];

  // Client-side order-number search (works across all FY ranges)
  const filteredRows = orderSearch.trim()
    ? rows.filter(r => {
        const q = orderSearch.trim().replace(/^#/, '').toLowerCase();
        return (
          String(r.order_number || '').toLowerCase().includes(q) ||
          String(r.shopify_order_id || '').toLowerCase().includes(q) ||
          String(r.vendor_name || '').toLowerCase().includes(q)
        );
      })
    : rows;

  // ── Render ────────────────────────────────────────────────────────────────

  const selectCls = "bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300";
  const inputCls  = "bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300";

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <FinanceNavTabs />

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-gray-900 text-2xl font-bold">Finance Register</h1>
            <p className="text-gray-500 text-sm mt-0.5">Formula-based ledger — columns match the account Excel sheet</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFormulaModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
              data-testid="formula-config-btn">
              <Settings size={14} /> Formula Config
            </button>
            <button onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
              data-testid="download-btn">
              <Download size={14} /> Download CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-end gap-3 flex-wrap mb-5 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
          data-testid="finance-register-filters">
          {/* Range type */}
          <div>
            <label className="text-gray-600 text-xs font-medium block mb-1.5">Period</label>
            <div className="flex items-center gap-0 bg-gray-100 border border-gray-200 rounded-lg p-0.5">
              {[{ v: 'fy', l: 'FY / Month' }, { v: 'alltime', l: 'All Time' }, { v: 'custom', l: 'Custom' }].map(o => (
                <button key={o.v} onClick={() => setRangeType(o.v)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    rangeType === o.v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`range-type-${o.v}`}>{o.l}</button>
              ))}
            </div>
          </div>

          {rangeType === 'fy' && (
            <>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">Financial Year</label>
                <select value={fy} onChange={e => setFy(e.target.value)} className={selectCls}
                  data-testid="fy-select">
                  {fyOptions.map(f => <option key={f} value={f}>FY {f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">Month</label>
                <select value={month || ''} onChange={e => setMonth(e.target.value ? Number(e.target.value) : null)}
                  className={selectCls} data-testid="month-select">
                  <option value="">All Months</option>
                  {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </>
          )}

          {rangeType === 'custom' && (
            <>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">From</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className={inputCls} data-testid="custom-start" />
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">To</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className={inputCls} data-testid="custom-end" />
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            {/* Order number / vendor search */}
            <div>
              <label className="text-gray-600 text-xs font-medium block mb-1.5">Search Order / Vendor</label>
              <input
                type="text"
                placeholder="#1035 or vendor name"
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                className={inputCls + " w-48"}
                data-testid="order-search-input"
              />
            </div>
            <div>
              <label className="text-gray-600 text-xs font-medium block mb-1.5">View</label>
              <div className="flex items-center gap-0 bg-gray-100 border border-gray-200 rounded-lg p-0.5">
              <button onClick={() => setViewMode('order')}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  viewMode === 'order' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-order-btn">Order</button>
              <button onClick={() => setViewMode('vendor')}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  viewMode === 'vendor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-vendor-btn">Vendor</button>
            </div>
            </div>
            <div>
              <label className="text-gray-600 text-xs font-medium block mb-1.5">&nbsp;</label>
              <button onClick={load} disabled={loading}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg disabled:opacity-50 shadow-sm font-medium"
                data-testid="view-btn">
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                View
              </button>
            </div>
          </div>
        </div>

        {/* Simulation banner */}
        {Object.keys(simulatedRefunds).length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm mb-4"
            data-testid="simulation-banner">
            <AlertTriangle size={14} />
            <span><strong>{Object.keys(simulatedRefunds).length}</strong> order(s) in simulation mode — no DB changes.</span>
            <button onClick={() => setSimulatedRefunds({})}
              className="ml-auto text-blue-500 hover:text-blue-700 text-xs underline">Clear</button>
          </div>
        )}

        {/* Summary tiles */}
        <SummaryStrip totals={totals} onNeedsRefundClick={() => setSweepOpen(true)} />

        {/* Order table */}
        {viewMode === 'order' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            data-testid="finance-register-table">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {[
                      ['Order #',         'text-left'],
                      ['Date',            'text-left'],
                      ['Vendor',          'text-left'],
                      ['GST/PAN',         'text-left'],
                      ['Status',          'text-left'],
                      ['Fin. Status',     'text-left'],
                      ['Refund Status',   'text-left'],
                      ['Reversal',        'text-left'],
                      ['Gross Amt',       'text-right'],
                      ['Refund',          'text-right'],
                      ['Comm %',          'text-right'],
                      ['Commission Amt',  'text-right'],
                      ['Commission Invoice', 'text-right'],
                      ['Comm Waiver',     'text-right'],
                      ['Comm GST (18%)',  'text-right'],
                      ['GST on Aniyor Logistics', 'text-right'],
                      ['Comm GST Rev',    'text-right'],
                      ['TCS (0.5%)',      'text-right'],
                      ['TDS (0.1%)',      'text-right'],
                      ['PG Fee',          'text-right'],
                      ['GST on PG Fee',   'text-right'],
                      ['Net Payout',      'text-right'],
                    ].map(([label, align]) => (
                      <th key={label}
                        className={`px-3 py-2.5 ${align} text-gray-600 font-semibold whitespace-nowrap`}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={20} className="px-3 py-12 text-center text-gray-400">
                      <RefreshCw size={16} className="inline animate-spin mr-2" />Loading...
                    </td></tr>
                  ) : filteredRows.length === 0 && rows.length === 0 ? (
                    <tr><td colSpan={20} className="px-3 py-12 text-center text-gray-400">
                      No orders found for the selected period.{' '}
                      <button className="text-indigo-600 underline hover:no-underline"
                        onClick={() => setRangeType('alltime')}>
                        Switch to All Time
                      </button>
                    </td></tr>
                  ) : filteredRows.length === 0 ? (
                    <tr><td colSpan={20} className="px-3 py-12 text-center text-gray-400">
                      No orders match "{orderSearch}".{' '}
                      {rangeType !== 'alltime' && (
                        <button className="text-indigo-600 underline hover:no-underline"
                          onClick={() => { setRangeType('alltime'); setOrderSearch(''); }}>
                          Search All Time instead
                        </button>
                      )}
                    </td></tr>
                  ) : filteredRows.map(rawRow => {
                    const r   = displayRow(rawRow);
                    const sim = r._simulated;
                    const isLogistics = r.is_logistics;
                    const isParent    = r.is_parent;
                    const rowBg = sim
                      ? 'bg-blue-50'
                      : isParent
                        ? 'bg-gray-50 border-b-2 border-gray-200'
                        : isLogistics
                          ? 'bg-amber-50/60'
                          : r.financial_status === 'refunded'
                            ? 'bg-red-50/40'
                            : 'bg-white hover:bg-gray-50';

                    return (
                      <tr key={r.order_id}
                        className={`border-b border-gray-100 transition-colors ${rowBg}`}
                        data-testid={`register-row-${r.order_number}`}>

                        {/* Order # */}
                        <td className="px-3 py-2 font-mono whitespace-nowrap">
                          {r.is_child ? (
                            <span className="flex items-center gap-1">
                              <span className="text-gray-300 select-none text-xs">└─</span>
                              <span className={`${isLogistics ? 'text-gray-400 italic' : 'text-indigo-600'} font-medium`}>
                                {r.order_number}
                              </span>
                            </span>
                          ) : (
                            <span className="text-indigo-700 font-bold">{r.order_number}</span>
                          )}
                          {r.fr_month_override && (
                            <span
                              className="inline-flex items-center gap-1 ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5"
                              title={`Overridden to ${r.fr_month_override.override_month} (was ${r.fr_month_override.original_month || '—'}). ${r.fr_month_override.reason || ''} — by ${r.fr_month_override.created_by || '—'}`}
                              data-testid={`fr-override-badge-${r.order_id}`}
                            >
                              <Clock size={9} /> Overridden
                              {isSuperAdmin && (
                                <button
                                  onClick={() => handleRevertFrOverride(r)}
                                  className="ml-0.5 text-amber-700 hover:text-red-600"
                                  title="Revert override (super admin only)"
                                  data-testid={`fr-override-revert-btn-${r.order_id}`}
                                >
                                  <RotateCcw size={9} />
                                </button>
                              )}
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.date}</td>

                        <td className="px-3 py-2 whitespace-nowrap max-w-[140px] truncate" title={r.vendor_name}>
                          <span className={
                            isParent
                              ? 'text-gray-500 italic text-xs font-medium'
                              : isLogistics
                                ? 'text-gray-400 italic text-xs'
                                : 'text-gray-700'
                          }>
                            {r.vendor_name}
                          </span>
                        </td>

                        {/* GST/PAN — sub-text shows the state used for the CGST/SGST
                            vs IGST liability split, so filing never needs opening the
                            invoice to look up a vendor's tax ID or state. */}
                        <td className="px-3 py-2 whitespace-nowrap" data-testid={`gst-pan-cell-${r.order_number}`}>
                          {isParent || isLogistics || !r.vendor_tax_id ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <div className="leading-tight">
                              <div className="font-mono text-gray-800 flex items-center gap-1">
                                <span className="text-[9px] font-semibold text-gray-400 uppercase">{r.vendor_tax_id_type}</span>
                                {r.vendor_tax_id}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {r.is_intrastate ? 'Maharashtra' : 'India (IGST)'}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-3 py-2 text-gray-500 capitalize whitespace-nowrap">{r.status}</td>

                        <td className="px-3 py-2 whitespace-nowrap">
                          {sim ? (
                            <span className="px-2 py-0.5 rounded text-xs border bg-blue-100 text-blue-700 border-blue-200 font-medium">
                              simulated
                            </span>
                          ) : isParent ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500 border border-gray-200 italic">
                              {r.financial_status === 'mixed' ? 'Mixed' : r.financial_status || '—'}
                            </span>
                          ) : (
                            <FinStatusCell row={r} onStatusChange={handleStatusChange} />
                          )}
                        </td>

                        {/* Refund Status + Reversal Status (iter-255-p0-c) */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {r.refund_status_detail ? (
                            <span className="px-2 py-0.5 rounded text-xs border bg-blue-50 text-blue-700 border-blue-200 font-medium capitalize"
                              data-testid={`refund-status-${r.order_number}`}>
                              {r.refund_status_detail}
                            </span>
                          ) : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <ReversalStatusBadge status={r.reversal_status} eligible={r.reversal_eligible} />
                        </td>

                        <td className="px-3 py-2 text-right text-gray-900 font-mono font-medium">
                          {fmtGross(r.gross)}
                        </td>

                        <td className="px-3 py-2 text-right font-mono">
                          {r.refund_amount > 0 ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-red-600 font-medium">{`₹${Number(r.refund_amount).toLocaleString('en-IN', {minimumFractionDigits: 2})}`}</span>
                              {r.refund_details && (
                                <span className="relative group cursor-default">
                                  <span className="text-gray-400 hover:text-gray-600 text-xs select-none">ⓘ</span>
                                  <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left text-xs text-gray-700 font-normal">
                                    <div className="font-semibold text-gray-900 mb-1.5">Refund Details</div>
                                    {r.refund_details.source && <div className="flex justify-between py-0.5 border-b border-gray-100"><span className="text-gray-400">Source</span><span className="capitalize">{r.refund_details.source}</span></div>}
                                    {r.refund_details.reason && <div className="flex justify-between py-0.5 border-b border-gray-100"><span className="text-gray-400">Reason</span><span className="max-w-32 text-right">{r.refund_details.reason}</span></div>}
                                    {r.refund_details.recorded_at && <div className="flex justify-between py-0.5"><span className="text-gray-400">Recorded</span><span>{new Date(r.refund_details.recorded_at).toLocaleDateString('en-IN')}</span></div>}
                                  </div>
                                </span>
                              )}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Commission % — SSOT badge: SUBSCRIPTION / HISTORICAL / OVERRIDE / MISSING PLAN */}
                        <td className="px-3 py-2 text-right font-mono relative">
                          {isLogistics || r.is_parent ? (
                            <span className="text-gray-300">—</span>
                          ) : r.commission_rate_source === 'MISSING_PLAN' ? (
                            <CommissionOverrideDropdown
                              row={r}
                              state={commOverrideState[r.order_id] || {}}
                              onToggle={() => setCommOverrideState(prev => ({
                                ...prev,
                                [r.order_id]: { ...prev[r.order_id], open: !(prev[r.order_id] || {}).open }
                              }))}
                              onApply={handleCommissionOverride}
                              onStateChange={(patch) => setCommOverrideState(prev => ({
                                ...prev,
                                [r.order_id]: { ...(prev[r.order_id] || {}), ...patch }
                              }))}
                            />
                          ) : (
                            <span className="group relative inline-flex items-center gap-1.5 justify-end">
                              <CommissionRateBadge row={r} />
                              {/* Correction pencil — always visible so the
                                  correction capability is discoverable
                                  (was hover-only, easy to mistake for "removed") */}
                              <button
                                title="Correct commission rate for this order"
                                onClick={() => setCommOverrideState(prev => ({
                                  ...prev,
                                  [r.order_id]: { ...(prev[r.order_id] || {}), open: !(prev[r.order_id] || {}).open }
                                }))}
                                className="text-amber-500 hover:text-amber-700 transition-colors"
                                data-testid={`edit-commission-${r.order_number}`}>
                                <Pencil size={11} />
                              </button>
                              {(commOverrideState[r.order_id] || {}).open && (
                                <CommissionOverrideDropdown
                                  row={r}
                                  isCorrectionMode
                                  state={commOverrideState[r.order_id] || {}}
                                  onToggle={() => setCommOverrideState(prev => ({
                                    ...prev,
                                    [r.order_id]: { ...prev[r.order_id], open: false }
                                  }))}
                                  onApply={handleCommissionOverride}
                                  onStateChange={(patch) => setCommOverrideState(prev => ({
                                    ...prev,
                                    [r.order_id]: { ...(prev[r.order_id] || {}), ...patch }
                                  }))}
                                />
                              )}
                            </span>
                          )}
                        </td>

                        {/* Commission Amt */}
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtAmt(r.commission_amt)}</td>

                        {/* Commission Invoice — only ever set for rows where a
                            vendor commission was actually charged (never logistics) */}
                        <td className="px-3 py-2 text-right font-mono">
                          {r.commission_invoice_number ? (
                            <button
                              onClick={() => previewCommissionInvoice(r.commission_invoice_id)}
                              className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                              title="Open this Commission Invoice"
                              data-testid={`commission-invoice-link-${r.order_number}`}>
                              {r.commission_invoice_number}
                            </button>
                          ) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Comm Waiver */}
                        <td className="px-3 py-2 text-right font-mono">
                          {r.comm_waiver > 0
                            ? <span className="text-yellow-600">{`₹${Number(r.comm_waiver).toLocaleString('en-IN', {minimumFractionDigits: 2})}`}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Comm GST — vendor commission GST ONLY. Logistics GST
                            moved to its own dedicated column (was previously
                            conflated into this one for the Aniyor-logistics row). */}
                        <td className="px-3 py-2 text-right font-mono text-gray-700">
                          {isLogistics ? <span className="text-gray-300">—</span> : fmtAmt(r.commission_gst)}
                        </td>

                        {/* GST on Aniyor Logistics — separate revenue stream from
                            vendor commission; only ever populated for the
                            Aniyor-logistics row. */}
                        <td className="px-3 py-2 text-right font-mono text-gray-700" title="GST Aniyor owes on its own logistics/shipping fee">
                          {isLogistics ? fmtAmt(r.logistics_gst) : <span className="text-gray-300">—</span>}
                        </td>

                        {/* Comm GST Rev */}
                        <td className="px-3 py-2 text-right font-mono">
                          {r.comm_gst_reversal > 0
                            ? <span className="text-yellow-600">{`₹${Number(r.comm_gst_reversal).toLocaleString('en-IN', {minimumFractionDigits: 2})}`}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>

                        <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtAmt(r.tcs)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtAmt(r.tds)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtAmt(r.pg_fee)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtAmt(r.pg_fee_gst)}</td>

                        {/* Net Payout — from V2 engine */}
                        <td className="px-3 py-2 text-right font-mono font-semibold">
                          {isLogistics
                            ? <span className="text-gray-300">—</span>
                            : r.net_vendor_payout > 0
                              ? <span className="text-emerald-700">{`₹${Number(r.net_vendor_payout).toLocaleString('en-IN', {minimumFractionDigits: 2})}`}</span>
                              : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals row — colSpan=8 covers Order#..Reversal (cols 1-8, now
                      including the GST/PAN column) so the figures below line up
                      under their real headers (Gross Amt onward) */}
                  {filteredRows.length > 0 && (
                    <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold text-gray-900"
                      data-testid="totals-row">
                      <td colSpan={8} className="px-3 py-3 text-xs uppercase tracking-wider text-gray-600">
                        TOTAL — {orderSearch.trim() ? `${filteredRows.length} of ` : ''}{totals.order_count} orders
                      </td>
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.gross_sales)}</td>
                      <td className="px-3 py-3 text-right font-mono text-red-600">
                        {totals.total_refunds > 0 ? `₹${Number(totals.total_refunds).toLocaleString('en-IN', {minimumFractionDigits: 2})}` : '—'}
                      </td>
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.commission_earned)}</td>
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.commission_gst)}</td>
                      <td className="px-3 py-3 text-right font-mono" title="Total GST on Aniyor's own logistics fee">{fmtGross(totals.logistics_gst)}</td>
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.tcs)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.tds)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.pg_fees)}</td>
                      <td className="px-3 py-3 text-right font-mono">{fmtGross(totals.pg_fee_gst)}</td>
                      <td className="px-3 py-3 text-right font-mono text-emerald-700">{fmtGross(totals.total_net_vendor_payout)}</td>
                    </tr>
                  )}

                  {/* GST liability breakdown — CGST / SGST / IGST split of the TCS,
                      Commission GST, and Logistics GST totals above (Payment
                      Gateway GST excluded: Razorpay already charges & collects
                      that, it's not Aniyor's liability). */}
                  {filteredRows.length > 0 && ['cgst', 'sgst', 'igst'].map((key) => (
                    <tr key={key} className="bg-gray-50 text-gray-500 border-b border-gray-100"
                      data-testid={`gst-breakdown-row-${key}`}>
                      <td colSpan={14} className="px-3 py-1.5 text-right text-[11px] italic uppercase tracking-wider">
                        ↳ {key === 'igst' ? 'IGST (Inter-State)' : `${key.toUpperCase()} (Intra-State)`}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11px]"
                        data-testid={`comm-gst-${key}-total`}>
                        {fmtGross(totals[`commission_gst_${key}`])}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11px]"
                        data-testid={`logistics-gst-${key}-total`}>
                        {fmtGross(totals[`logistics_gst_${key}`])}
                      </td>
                      <td className="px-3 py-1.5" />
                      <td className="px-3 py-1.5 text-right font-mono text-[11px]"
                        data-testid={`tcs-${key}-total`}>
                        {fmtGross(totals[`tcs_${key}`])}
                      </td>
                      <td colSpan={4} className="px-3 py-1.5" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Vendor summary */}
        {viewMode === 'vendor' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            data-testid="vendor-summary-table">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Vendor','Orders','Gross Amt','Refund','Commission Amt','Comm Waiver','Comm GST','TCS','TDS','PG Fee','Net Payout'].map((h, i) => (
                      <th key={h} className={`px-3 py-2.5 ${i === 0 ? 'text-left' : 'text-right'} text-gray-600 font-semibold whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={11} className="px-3 py-12 text-center text-gray-400">Loading...</td></tr>
                  ) : vendorSummary.length === 0 ? (
                    <tr><td colSpan={11} className="px-3 py-12 text-center text-gray-400">No data</td></tr>
                  ) : vendorSummary.map(vs => (
                    <tr key={vs.vendor_name}
                      className="border-b border-gray-100 bg-white hover:bg-gray-50"
                      data-testid={`vendor-row-${vs.vendor_name}`}>
                      <td className="px-3 py-2.5 text-gray-900 font-medium">{vs.vendor_name}</td>
                      <td className="px-3 py-2.5 text-right text-gray-600">{vs.order_count}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-900 font-medium">{fmtGross(vs.gross)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {vs.refund_amount > 0 ? <span className="text-red-600">{fmtGross(vs.refund_amount)}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtAmt(vs.commission_amt)}</td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {vs.comm_waiver > 0 ? <span className="text-yellow-600">{fmtGross(vs.comm_waiver)}</span> : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtAmt(vs.commission_gst)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtAmt(vs.tcs)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtAmt(vs.tds)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-gray-700">{fmtAmt(vs.pg_fee)}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-semibold">{fmtGross(vs.net_vendor_payout)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showFormulaModal && (
        <FormulaConfigModal onClose={() => setShowFormulaModal(false)} apiCall={apiCall} />
      )}
      {refundIntent && (
        <RefundIntentModal
          order={refundIntent}
          onClose={() => setRefundIntent(null)}
          onCalculateOnly={handleCalculateOnly}
          onRecordRefund={handleRecordRefund}
          onProcessRefund={handleProcessRefund}
          onCheckEligibility={handleCheckEligibility}
        />
      )}
      {sweepOpen && (
        <ReconciliationSweepPanel
          apiCall={apiCall}
          onClose={() => setSweepOpen(false)}
          onOpenRefundModal={(item) => {
            setSweepOpen(false);
            setRefundIntent({ ...item, gross: item.gross_amount });
          }}
        />
      )}
    </DashboardLayout>
  );
}
